import React from 'react';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import Input from '@lib/components/Input/Input';
import { FieldItem } from '@lib/interfaces/form';
import { getFieldValidation } from '@lib/utils/validationUtils';
import ChecklistFieldNames from '@lib/enums/fieldNames/checklistFieldNames';
import Typography from '@lib/components/Typography/Typography';
import { InputSizes, InputTypes } from '@lib/components/Input/enums';
import { MAX_TEXT_FIELD_LENGTH } from '@lib/enums/form';
import ChecklistFieldOptions from 'components/FormTemplateBuilder/ChecklistFieldOptions/ChecklistFieldOptions';
import QuestionChecklistField from 'components/FormTemplateBuilder/QuestionChecklistField/QuestionChecklistField';
import ChecklistOptionsAddItemComponent from '../components/ChecklistOptionsAddItemComponent/ChecklistOptionsAddItemComponent';

type FormFieldsArgs = {
  t: TFunction<'translation', undefined>;
};

export const defaultValues = {
  [ChecklistFieldNames.DetailsTitle]: undefined,
  [ChecklistFieldNames.OptionsTitle]: undefined,
  [ChecklistFieldNames.Name]: '',
  [ChecklistFieldNames.Description]: '',
  [ChecklistFieldNames.Options]: [],
};

export default function getChecklistFormFields({
  t,
}: FormFieldsArgs): FieldItem[] {
  return [
    {
      name: ChecklistFieldNames.DetailsTitle,
      customRender: () => (
        <Typography variant="h3" key="details">
          {t('details')}
        </Typography>
      ),
    },
    {
      name: ChecklistFieldNames.Name,
      label: t('checklist-name'),
      element: Input,
      validation: getFieldValidation({ t, required: true }),
    },
    {
      name: ChecklistFieldNames.Description,
      label: t('description'),
      element: Input,
      componentProps: {
        type: InputTypes.textarea,
      },
      validation: getFieldValidation({ t }),
    },
    {
      name: ChecklistFieldNames.Options,
      label: t('checklist-options'),
      subFields: [
        {
          name: ChecklistFieldNames.OptionValues,
          element: Input,
          validation: Yup.array()
            .of(
              Yup.string()
                .trim()
                .max(MAX_TEXT_FIELD_LENGTH, t('max-text-length-field-error'))
                .required(t('required-field-error')),
            )
            .min(1, t('required-field-error'))
            .required(t('required-field-error')),
          componentProps: {
            hasBorder: false,
            hasErrorAsTooltip: true,
            placeholder: t('checklist-option-placeholder'),
            size: InputSizes.medium,
          },
          hideErrors: true,
        },
      ],
      hideErrors: true,
      subFieldsEntity: 'option',
      subFieldsMinLength: 1,
      fieldComponents: {
        FieldArrayAddItemComponent: ChecklistOptionsAddItemComponent,
        FieldArrayEmptyStateComponent: ChecklistOptionsAddItemComponent,
        FieldArrayLoopComponent: QuestionChecklistField,
        FieldArrayItemWrapperComponent: ChecklistFieldOptions,
      },
    },
  ];
}
